/**
 * Sound Map — shared translation helpers.
 *
 * All keys are LITERAL strings (literal-i18n-keys rule): dynamic values
 * (state, section type) go through exhaustive switches, never template
 * interpolation of the key itself.
 *
 * @see docs/specs/mobile/sound-map.mobile.md
 */

import type { TFunction } from 'i18next';
import type { SoundMapPhoneme, SoundMapResponse } from '../../generated/api/http-contracts';

export type PhonemeState = SoundMapPhoneme['state'];
export type PhonemeType = SoundMapPhoneme['type'];

/**
 * Contrastive difficulty at or above this marks a sound as "expected
 * hard for your Spanish" (badge on untried chips). Fixed by the mobile
 * spec — the mastery *state* itself is always server-computed.
 */
export const EXPECTED_HARD_MIN_DIFFICULTY = 4;

/** Badge rule per spec: expected-hard hint shows only on untried sounds. */
export function showsExpectedHardBadge(phoneme: SoundMapPhoneme): boolean {
  return (
    phoneme.state === 'untried' &&
    phoneme.difficulty !== null &&
    phoneme.difficulty >= EXPECTED_HARD_MIN_DIFFICULTY
  );
}

/**
 * Does this map have a progress dimension at all?
 *
 * `progress: 'off'` means the speech provider never names WHICH phoneme was
 * mispronounced for this target variant (Azure does that for en-US only), so no
 * attempt can be attributed to a sound and the map can never fill. The server
 * already reports every phoneme as untried in that case; this is what stops the
 * screen from drawing a bar that cannot move.
 *
 * Absent capabilities mean an older backend, which only ever served variants
 * where progress was real — so the default is `true` and an N-1 client keeps
 * today's screen exactly.
 *
 * @see docs/specs/api/target-capability-profile.api.md (TCP-001, TCP-006)
 */
export function showsProgress(data: Pick<SoundMapResponse, 'capabilities'>): boolean {
  return data.capabilities?.progress !== 'off';
}

/** Section title for a phoneme type (inventory order sections). */
export function translateSectionTitle(t: TFunction, type: PhonemeType): string {
  switch (type) {
    case 'consonant': return t('soundMap.sections.consonants');
    case 'vowel': return t('soundMap.sections.vowels');
    case 'diphthong': return t('soundMap.sections.diphthongs');
    case 'tone': return t('soundMap.sections.tones');
  }
}

/** User-facing label for a mastery state (detail sheet pill). */
export function translateStateLabel(t: TFunction, state: PhonemeState): string {
  switch (state) {
    case 'mastered': return t('soundMap.states.mastered');
    case 'learning': return t('soundMap.states.learning');
    case 'weak': return t('soundMap.states.weak');
    case 'untried': return t('soundMap.states.untried');
  }
}

/**
 * Spoken form of a chip for VoiceOver/TalkBack, e.g. "θ, weak, 54 percent".
 * Untried sounds that are expected to be hard get the extended label.
 */
export function chipA11yLabel(t: TFunction, phoneme: SoundMapPhoneme): string {
  const symbol = phoneme.ipaSymbol;
  const score = phoneme.avgScore ?? 0;
  switch (phoneme.state) {
    case 'mastered': return t('soundMap.chip.a11yMastered', { symbol, score });
    case 'learning': return t('soundMap.chip.a11yLearning', { symbol, score });
    case 'weak': return t('soundMap.chip.a11yWeak', { symbol, score });
    case 'untried':
      return showsExpectedHardBadge(phoneme)
        ? t('soundMap.chip.a11yUntriedHard', { symbol })
        : t('soundMap.chip.a11yUntried', { symbol });
  }
}

/**
 * Plural-aware per-state counts joined as one line, e.g.
 * "3 mastered · 7 learning · 4 weak · 32 not tried".
 * Used by the screen header summary and the Practice tab card.
 *
 * Takes the whole response, not just `summary`: without progress every count is
 * zero and the line would read "0 mastered · 0 learning · 0 weak · 44 not
 * tried" — technically true, and it reads as a broken product. Both call sites
 * have to make this choice, so the type makes them.
 */
export function formatSummaryLine(t: TFunction, data: SoundMapResponse): string {
  // TCP-006: a capability that cannot be offered says what IS offered. Never a
  // percentage, a coverage figure, or the word "fallback" (TCP-005).
  if (!showsProgress(data)) return t('soundMap.guidanceOnly.summary');

  const { summary } = data;
  return [
    t('soundMap.summary.mastered', { count: summary.mastered }),
    t('soundMap.summary.learning', { count: summary.learning }),
    t('soundMap.summary.weak', { count: summary.weak }),
    t('soundMap.summary.untried', { count: summary.untried }),
  ].join(' · ');
}
